/**
 * OTD Override Panel — CR-5263.
 *
 * Supplier-facing toggle that forces OTD on or off for a single PO line,
 * regardless of the platform-level OTD rules. Hidden when the platform
 * disables line-level overrides.
 */
import { useEffect, useState } from 'react';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { purchasingAPI, platformSettingsAPI, PurchaseOrderLine } from '../../lib/api';

interface OTDOverridePanelProps {
  line: PurchaseOrderLine;
  onUpdate?: () => void;
}

export default function OTDOverridePanel({ line, onUpdate }: OTDOverridePanelProps) {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [overridden, setOverridden] = useState<boolean>(!!line.otd_overridden);
  const [overrideValue, setOverrideValue] = useState<boolean>(!!line.otd_override_value);
  const [reason, setReason] = useState<string>(line.otd_override_reason || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await platformSettingsAPI.getSettings();
        if (cancelled) return;
        setAllowed(res.data.otd_allow_line_level_override ?? true);
      } catch {
        if (!cancelled) setAllowed(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Re-sync local state if the line prop changes (e.g., after refetch).
  useEffect(() => {
    setOverridden(!!line.otd_overridden);
    setOverrideValue(!!line.otd_override_value);
    setReason(line.otd_override_reason || '');
  }, [line.id, line.otd_overridden, line.otd_override_value, line.otd_override_reason]);

  if (allowed === null) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
        <Loader2 className="h-3 w-3 animate-spin" />
        Checking OTD override settings…
      </div>
    );
  }
  if (!allowed) return null;

  const dirty =
    overridden !== !!line.otd_overridden
    || (overridden && overrideValue !== !!line.otd_override_value)
    || (overridden && (reason || '').trim() !== (line.otd_override_reason || '').trim());

  const save = async () => {
    setSaving(true);
    try {
      await purchasingAPI.setLineOTDOverride(line.id, {
        overridden,
        override_value: overridden ? overrideValue : null,
        reason: overridden ? reason.trim() : '',
      });
      toast.success(overridden ? 'OTD override applied' : 'OTD override cleared');
      onUpdate?.();
    } catch (err: unknown) {
      console.error('Failed to save OTD override', err);
      toast.error('Failed to save OTD override');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-amber-50/50 border-amber-200">
      <div className="flex items-start gap-2 mb-3">
        <ShieldAlert className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
        <div>
          <Label className="text-sm font-medium text-amber-900">OTD manual override</Label>
          <p className="text-xs text-amber-800 mt-0.5">
            Forces the OTD verdict for this line regardless of receipt date or
            promise-date history. Use sparingly.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between py-2">
        <Label htmlFor={`otd-override-${line.id}`} className="text-sm">
          Override active
        </Label>
        <Switch
          id={`otd-override-${line.id}`}
          checked={overridden}
          onCheckedChange={setOverridden}
          disabled={saving}
        />
      </div>

      {overridden && (
        <>
          <div className="flex items-center justify-between py-2 border-t border-amber-200">
            <Label htmlFor={`otd-value-${line.id}`} className="text-sm">
              Forced verdict
            </Label>
            <div className="flex items-center gap-2">
              <span className={`text-xs ${overrideValue ? 'text-emerald-700 font-medium' : 'text-muted-foreground'}`}>
                On time
              </span>
              <Switch
                id={`otd-value-${line.id}`}
                checked={!overrideValue}
                onCheckedChange={(v: boolean) => setOverrideValue(!v)}
                disabled={saving}
              />
              <span className={`text-xs ${!overrideValue ? 'text-rose-700 font-medium' : 'text-muted-foreground'}`}>
                Late
              </span>
            </div>
          </div>
          <div className="pt-2 border-t border-amber-200">
            <Label htmlFor={`otd-reason-${line.id}`} className="text-xs text-amber-900">
              Reason (audit trail)
            </Label>
            <textarea
              id={`otd-reason-${line.id}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="mt-1 w-full text-sm border border-amber-200 rounded px-2 py-1 bg-white"
              placeholder="Why is OTD being overridden for this line?"
              disabled={saving}
            />
          </div>
        </>
      )}

      <div className="flex justify-end mt-3">
        <Button
          size="sm"
          onClick={save}
          disabled={!dirty || saving}
        >
          {saving ? (<><Loader2 className="h-3 w-3 mr-2 animate-spin" />Saving</>) : 'Save'}
        </Button>
      </div>
    </div>
  );
}
